import React, { useEffect, useMemo, useState } from 'react';
import { platformOptions } from '../../constants';
import { Justify, Segment } from 'tea-component';

import moment, { Moment } from 'moment';
import { getTrendsCondList } from '@/pages/opinion/services/trends';
import CountrySingleSelecter from '@/pages/opinion/components/CountrySingleSelecter';
import RangePicker from '@/pages/home/components/RangePicker';
import { getCountryFlag } from '@/pages/home/_config/countries';
import './index.scss';
import {
  dailyFormat,
  dailyToDay,
  dayToDaily,
} from '@/pages/opinion/components/FilterCommon/constants/date';
import { getLocale, useIntl } from 'umi';
import useStorageOperation from '@/pages/opinion/hooks/useStorageOperation';
import useTabHistory from '@/pages/opinion/hooks/useTabHistory';

type PlatformType = 'google' | 'twitter';

export default function TopFilter({
  changeCondition,
  params,
}: {
  changeCondition: (value: { [key: string]: string }) => void;
  params: {
    start_time: string;
    end_time: string;
    region: string;
    origin: PlatformType;
  };
  gameInfo: any;
}) {
  const [timeValue, setTimeValue] = useState<Array<string>>([
    params.start_time,
    params.end_time,
  ]);
  const { formatMessage } = useIntl();
  const [dateType, setDateType] = useState('daily');
  const [countryMap, setCountryMap] = useState<{ [key: string]: Array<any> }>({
    google: [],
    twitter: [],
  });

  const { tab: platform, onActive } = useTabHistory<PlatformType>({
    defaultValue: 'google',
  });
  // const [platform, setPlatform] = useState<PlatformType>('google');

  const { data, setStore } = useStorageOperation({
    storage: sessionStorage,
    storageKey: 'google_twitter_storage',
    model: 'cover',
    defaultValue: ['region', 'city'].reduce((pre, cur) => {
      return {
        ...pre,
        [cur]: {
          google: '',
          twitter: '',
        },
      };
    }, {}),
  });

  const [region, setRegion] = useState<{
    [key in PlatformType]: string;
  }>(data.region);
  const [city, setCity] = useState<{ [key in PlatformType]: string }>(
    data.city,
  );

  // 时间选择器修改
  const onCloseRangePicker = (value: Array<Moment>, type: string) => {
    let dateType = dayToDaily[type];
    setDateType(dateType);
    const hasTime = value.every(Boolean);
    let newValue = hasTime ? value : timeValue;
    let timeList = newValue.map((item) => {
      return (typeof item === 'string' ? moment(item) : item).format(
        dailyFormat[dateType],
      );
    });
    setTimeValue(timeList);
    changeCondition({
      start_time: timeList[0],
      end_time: timeList[1],
      date_type: dateType,
    });
  };

  function onChange(
    {
      region: regionValue,
      city: cityValue,
      platform: platformValue = platform,
    }: {
      region: string;
      city: string;
      platform?: PlatformType;
    },
    isRequest = true,
  ) {
    const regionInfo = { ...region, [platformValue]: regionValue };
    const cityInfo = { ...city, [platformValue]: cityValue };
    setStore({
      region: regionInfo,
      city: cityInfo,
    });
    setRegion(regionInfo);
    setCity(cityInfo);

    if (isRequest) {
      changeCondition({
        region: regionValue,
        city: cityValue,
        origin: platformValue,
      });
    }
  }

  // 获取筛选器数据
  const getTrendsCondListFormServer = () => {
    getTrendsCondList().then((data: any) => {
      let map: { [key: string]: Array<any> } = { google: [], twitter: [] };
      data?.forEach(({ origin, country }: any) => {
        map[origin] = country;
        // 首次加载列表数据，这里设置一下region和city的默认值
        if (!region?.[platform]) {
          let defaultItem = country?.[0] || {};
          const lowRegionValue =
            defaultItem?.country_name_abbre?.toLocaleLowerCase() || 'br';
          const city = defaultItem.city?.[0] || '';
          onChange({ region: lowRegionValue, city: city });
        } else {
          onChange({ region: region[platform], city: city[platform] });
        }
      });
      setCountryMap(map);
    });
  };

  useEffect(() => {
    getTrendsCondListFormServer();
  }, []);

  // 添加页面埋点
  const addOperationLog = async () => {};

  useEffect(() => {
    addOperationLog();
  }, [platform]);

  const optionsMap: { [key in PlatformType]: Array<any> } = useMemo(() => {
    return ['google', 'twitter'].reduce(
      (pre, cur) => {
        const item = countryMap?.[cur];
        let result: { country_icon: any; country_name: any; city_list: any }[] =
          [];
        if (item && Array.isArray(item)) {
          result = item.map((item) => {
            const lowValue = item.country_name_abbre.toLocaleLowerCase();

            return {
              country_icon: getCountryFlag(lowValue),
              country_name: lowValue,
              city_list: item?.city || [],
            };
          });
        }
        return { ...pre, [cur]: result };
      },
      { google: [], twitter: [] },
    );
  }, [countryMap]);

  // 平台切换
  const platformChange = (value: PlatformType) => {
    onActive({ id: value });
    let regionItem = region[value];
    let cityItem = city[value];

    if (!regionItem) {
      const item = countryMap?.[value]?.[0];
      regionItem = item.country_name_abbre?.toLocaleLowerCase() || '';
    }

    if (!cityItem) {
      const item = countryMap?.[value]?.[0];
      const cities = item?.city || [''];
      cityItem = cities[0];
    }

    onChange({
      region: regionItem,
      city: cityItem,
      platform: value,
    });
  };

  const rangePickerValue = useMemo(() => {
    return timeValue.map((time) => moment(time));
  }, [timeValue]);

  return (
    <Justify
      className="top-filter-wrapper trends-justify trends-justify--top"
      left={
        <>
          {/* 该日期组件参考经分，重构此处仅占位*/}
          <div className="search-item search-item-date">
            <RangePicker
              startOfTime="2017-01-01"
              className="search-border"
              typeList={['date', 'week', 'month']}
              showSelectType={true}
              startOfWeek={1}
              language={getLocale() === 'zh-CN' ? 'cn' : 'en'}
              value={rangePickerValue}
              type={dailyToDay[dateType]}
              onChange={(value: any, type: any) =>
                onCloseRangePicker(value, type)
              }
            />
          </div>

          {/* 国家单选筛选 */}
          <CountrySingleSelecter
            secondary={platform === 'twitter'}
            region={region[platform]}
            optionsData={optionsMap[platform]}
            onChange={onChange}
            city={city[platform]}
          />
          <Segment
            value={platform}
            className="dbo-trends-segment segment-light"
            onChange={platformChange}
            options={platformOptions(formatMessage)}
          />
        </>
      }
    />
  );
}
